import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, AlertTriangle, Trophy, Clock, Calendar, MapPin } from 'lucide-react';
import { PlayerGameDayStatus } from './PlayerGameDayStatus';
import { TeamLogo } from './TeamLogo';
import { supabase } from '@/integrations/supabase/client';
import { gameRosterService } from '@/services/gameRosterService';
import { toast } from 'sonner';

type GameDayStatus = 'ready_to_play' | 'not_ready' | 'no_show' | 'injured' | 'substituted' | 'disqualified' | 'under_age';

interface VenueGameRosterModalProps {
  open: boolean;
  onClose: () => void;
  gameId: string;
  gameCode?: string;
  onRosterUpdate?: () => void;
}

interface GameData {
  id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  home_team_id?: number;
  away_team_id?: number;
  home_team_label: string;
  away_team_label: string;
  game_code: string;
  sport_type?: string;
  venue_name?: string;
  minimum_players_required: number;
}

interface TeamPlayer {
  user_id: string;
  position?: string;
  joined_at: string;
  player_name: string;
  player_type: 'supabase' | 'internal';
  team_side: 'home' | 'away';
  game_day_status: GameDayStatus;
  roster_id?: string;
}

export const VenueGameRosterModal = ({ 
  open, 
  onClose, 
  gameId, 
  gameCode, 
  onRosterUpdate 
}: VenueGameRosterModalProps) => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamSide, setSelectedTeamSide] = useState<'home' | 'away'>('home');
  const [availablePlayers, setAvailablePlayers] = useState<any[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');

  // Load game data and roster
  useEffect(() => {
    if (open && gameId) {
      loadGameAndRoster();
    }
  }, [open, gameId]);

  const loadGameAndRoster = async () => {
    try {
      setLoading(true);
      
      // Load game data from venue_games
      const { data: game, error: gameError } = await supabase
        .from('venue_games')
        .select('*')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;

      // Get venue name
      let venueName = 'Unknown Venue';
      if (game.venue_id) {
        const { data: venue } = await supabase
          .from('venues')
          .select('venue_name')
          .eq('venue_id', game.venue_id)
          .single();
        venueName = venue?.venue_name || 'Unknown Venue';
      }

      // Get team names from database
      let homeTeamName = 'TBD';
      let awayTeamName = 'TBD';
      
      if (game.home_team_id) {
        const { data: homeTeam } = await supabase
          .from('teams')
          .select('team_name')
          .eq('team_id', game.home_team_id)
          .single();
        homeTeamName = homeTeam?.team_name || `Team ${game.home_team_id}`;
      }
      
      if (game.away_team_id) {
        const { data: awayTeam } = await supabase
          .from('teams')
          .select('team_name')
          .eq('team_id', game.away_team_id)
          .single();
        awayTeamName = awayTeam?.team_name || `Team ${game.away_team_id}`;
      }

      setGameData({
        id: game.id,
        game_title: game.game_title,
        game_date: game.game_date,
        game_time: game.game_time,
        home_team_id: game.home_team_id,
        away_team_id: game.away_team_id,
        home_team_label: homeTeamName,
        away_team_label: awayTeamName,
        game_code: game.game_code,
        sport_type: 'Basketball', // Default for now
        venue_name: venueName,
        minimum_players_required: 5
      });

      // Load existing roster
      const roster = await gameRosterService.loadGameRoster(gameId);
      
      // Always populate roster from teams if teams are assigned
      if (game.home_team_id || game.away_team_id) {
        await populateRosterFromTeams(game);
      }

      // Load current roster with player names and statuses
      await loadCurrentRoster();
      
    } catch (error) {
      console.error('Error loading game and roster:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  const populateRosterFromTeams = async (game: any) => {
    try {
      console.log('Populating roster for game:', gameId, 'Teams:', game.home_team_id, game.away_team_id);
      if (game.home_team_id || game.away_team_id) {
        await gameRosterService.populateGameRosterFromTeams(
          gameId, 
          game.home_team_id, 
          game.away_team_id
        );
        console.log('Roster populated successfully');
      } else {
        console.log('No teams assigned to populate roster');
      }
    } catch (error) {
      console.error('Error populating roster from teams:', error);
    }
  };

  const loadCurrentRoster = async () => {
    try {
      const roster = await gameRosterService.loadGameRoster(gameId);
      
      // Transform roster data to include game day status
      const playersWithStatus: TeamPlayer[] = roster.map(player => ({
        user_id: player.player_id,
        position: player.position,
        joined_at: new Date().toISOString(),
        player_name: player.player_name || `Player ${player.player_id.substring(0, 8)}`,
        player_type: player.player_type,
        team_side: player.team_side,
        game_day_status: player.is_present ? 'ready_to_play' : 'not_ready',
        roster_id: player.id
      }));

      setTeamPlayers(playersWithStatus);
      
    } catch (error) {
      console.error('Error loading current roster:', error);
    }
  };

  const loadAvailableTeamPlayers = async (teamId: number) => {
    try {
      const { data: players, error } = await supabase
        .from('team_players')
        .select(`
          user_id,
          position,
          joined_at
        `)
        .eq('team_id', teamId);

      if (error) throw error;

      // Filter out players already in roster
      const rosterPlayerIds = teamPlayers
        .filter(p => p.team_side === selectedTeamSide)
        .map(p => p.user_id);
      
      // Get player names separately
      const playerIds = players?.map(p => p.user_id) || [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', playerIds);

      const available = (players || [])
        .filter(p => !rosterPlayerIds.includes(p.user_id))
        .map(p => {
          const profile = profiles?.find(prof => prof.id === p.user_id);
          return {
            ...p,
            player_name: profile?.username || 
                        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
                        `Player ${p.user_id.substring(0, 8)}`
          };
        });

      setAvailablePlayers(available);
    } catch (error) {
      console.error('Error loading available players:', error);
      setAvailablePlayers([]);
    }
  };

  // Load available players when team side changes
  useEffect(() => {
    if (gameData && selectedTeamSide) {
      const teamId = selectedTeamSide === 'home' ? gameData.home_team_id : gameData.away_team_id;
      if (teamId) {
        loadAvailableTeamPlayers(teamId);
      }
    }
  }, [selectedTeamSide, gameData, teamPlayers]);

  const handleAddPlayer = async () => {
    if (!selectedPlayerId || !gameData) return;

    try {
      const teamId = selectedTeamSide === 'home' ? gameData.home_team_id : gameData.away_team_id;
      if (!teamId) return;

      await gameRosterService.addPlayerToGameRoster(
        gameId,
        teamId,
        selectedPlayerId,
        'supabase',
        selectedTeamSide
      );

      setSelectedPlayerId('');
      await loadCurrentRoster();
      toast.success('Player added to game roster');
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player');
    }
  };

  const handleStatusChange = async (playerId: string, status: GameDayStatus) => {
    try {
      const player = teamPlayers.find(p => p.user_id === playerId);
      if (!player?.roster_id) return;

      // Update roster presence based on status
      const isPresent = ['ready_to_play', 'substituted'].includes(status);
      await gameRosterService.updatePlayerPresence(player.roster_id, isPresent);

      // Update local state
      setTeamPlayers(prev => prev.map(p => 
        p.user_id === playerId ? { ...p, game_day_status: status } : p
      ));

      toast.success(`Player status updated to ${status.replace('_', ' ')}`);
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error updating player status:', error);
      toast.error('Failed to update player status');
    }
  };

  const handleRemovePlayer = async (playerId: string) => {
    try {
      const player = teamPlayers.find(p => p.user_id === playerId);
      if (!player?.roster_id) return;

      await gameRosterService.removePlayerFromGameRoster(player.roster_id);
      await loadCurrentRoster();
      toast.success('Player removed from roster');
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  const getPlayersByTeamSide = (teamSide: 'home' | 'away') => {
    return teamPlayers.filter(p => p.team_side === teamSide);
  };

  const getReadyPlayerCount = (teamSide: 'home' | 'away') => {
    return getPlayersByTeamSide(teamSide).filter(p => 
      ['ready_to_play', 'substituted'].includes(p.game_day_status)
    ).length;
  };

  if (!gameData) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-8">
            {loading ? 'Loading game data...' : 'Game not found'}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Trophy className="h-6 w-6 text-sports-green" />
              Game Roster Management
            </DialogTitle>
            <div className="text-lg font-semibold text-center">
              {gameData.game_title}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(gameData.game_date).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {gameData.game_time}
              </div>
              {gameData.venue_name && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {gameData.venue_name}
                </div>
              )}
              {gameData.sport_type && (
                <Badge variant="secondary">{gameData.sport_type}</Badge>
              )}
              <Badge variant="outline" className="font-mono">
                Code: {gameData.game_code}
              </Badge>
            </div>
          </DialogHeader>

          {/* Team Selection for Adding Players */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <label className="text-sm font-medium">Add Players to Team:</label>
            <Select value={selectedTeamSide} onValueChange={(value) => setSelectedTeamSide(value as 'home' | 'away')}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">
                  {gameData.home_team_label} - Home
                </SelectItem>
                <SelectItem value="away">
                  {gameData.away_team_label} - Away
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Add Player Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Add Player to {selectedTeamSide === 'home' ? gameData.home_team_label : gameData.away_team_label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a player to add" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlayers.length === 0 ? (
                      <SelectItem value="no-players" disabled>
                        No available players found
                      </SelectItem>
                    ) : (
                      availablePlayers.map((player) => (
                        <SelectItem key={player.user_id} value={player.user_id}>
                          {player.player_name}
                          {player.position && ` (${player.position})`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddPlayer} disabled={!selectedPlayerId || loading}>
                  {loading ? 'Adding...' : 'Add Player'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Roster */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Home Team */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{gameData.home_team_label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${getReadyPlayerCount('home') >= gameData.minimum_players_required ? 'text-sports-green' : 'text-destructive'}`}>
                      {getReadyPlayerCount('home')}/{gameData.minimum_players_required} Ready
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {getPlayersByTeamSide('home').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No players added yet
                  </p>
                ) : (
                  getPlayersByTeamSide('home').map((player) => (
                    <div key={player.user_id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/10">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback>
                            {player.player_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{player.player_name}</p>
                          {player.position && (
                            <p className="text-xs text-muted-foreground">{player.position}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PlayerGameDayStatus
                          playerId={player.user_id}
                          currentStatus={player.game_day_status}
                          onStatusChange={handleStatusChange}
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRemovePlayer(player.user_id)}
                          className="h-8 px-2"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Away Team */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{gameData.away_team_label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${getReadyPlayerCount('away') >= gameData.minimum_players_required ? 'text-sports-green' : 'text-destructive'}`}>
                      {getReadyPlayerCount('away')}/{gameData.minimum_players_required} Ready
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {getPlayersByTeamSide('away').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No players added yet
                  </p>
                ) : (
                  getPlayersByTeamSide('away').map((player) => (
                    <div key={player.user_id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/10">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback>
                            {player.player_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{player.player_name}</p>
                          {player.position && (
                            <p className="text-xs text-muted-foreground">{player.position}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PlayerGameDayStatus
                          playerId={player.user_id}
                          currentStatus={player.game_day_status}
                          onStatusChange={handleStatusChange}
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRemovePlayer(player.user_id)}
                          className="h-8 px-2"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Warnings */}
          {(getReadyPlayerCount('home') < gameData.minimum_players_required ||
            getReadyPlayerCount('away') < gameData.minimum_players_required) && (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  <p className="font-medium">Warning: Minimum player requirement not met</p>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Teams that don't meet the minimum player requirement ({gameData.minimum_players_required} players) 
                  may forfeit the game. Ensure both teams have enough ready players.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};